import React, { useState, useRef } from 'react';

type DocumentUploaderProps = {
  width?: string;
  height?: string;
  onFilesChange?: (files: File[]) => void; // Callback to parent
};

const allowedTypes = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 20;

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  width = '100%',
  height = '170px',
  onFilesChange,
}) => {
  const [error, setError] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatSize = (bytes: number) => {
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = e.target.files;
    if (!newFiles) return;

    setError('');
    const selected = Array.from(newFiles);

    if (selected.length + files.length > MAX_FILES) {
      setError(`You can only upload up to ${MAX_FILES} files total.`);
      return;
    }

    for (const file of selected) {
      const sizeMB = file.size / (1024 * 1024);
      if (!allowedTypes.includes(file.type)) {
        setError(`Unsupported file type: ${file.name}`);
        return;
      }
      if (sizeMB > MAX_FILE_SIZE_MB) {
        setError(`"${file.name}" exceeds 20MB limit.`);
        return;
      }
    }

    const combinedFiles = [...files, ...selected];
    setFiles(combinedFiles);
    onFilesChange?.(combinedFiles); // Notify parent
  };

  const removeFile = (index: number) => {
    const updated = [...files];
    updated.splice(index, 1);
    setFiles(updated);
    onFilesChange?.(updated); // Notify parent
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      style={{
        width,
        height,
        border: '2px dashed var(--primary)',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        cursor: 'pointer',
        backgroundColor: '#f9f9f9',
        padding: '16px',
        textAlign: 'center',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <input
        type="file"
        accept={allowedTypes.join(',')}
        multiple
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <>
        <div style={{ fontSize: '32px', color: 'var(--primary)', fontWeight: 700 }}>
          +
        </div>
        <div style={{ fontSize: '14px', color: 'var(--primary)' }}>
          Click to select documents
        </div>
        <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
          Max size: 20MB per file | Max {MAX_FILES} files
        </div>
      </>

      {files.length > 0 && (
        <div
          style={{
            marginTop: '12px',
            width: '100%',
            maxHeight: '120px',
            overflowY: 'auto',
          }}
        >
          {files.map((file, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '13px',
                margin: '4px 0',
                padding: '4px 8px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <span style={{ flex: 1 }}>{file.name}</span>
              <span style={{ color: '#777', marginLeft: '8px' }}>
                {formatSize(file.size)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(i);
                }}
                style={{
                  marginLeft: '12px',
                  background: 'transparent',
                  color: 'red',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ❌
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            bottom: '8px',
            fontSize: '12px',
            color: 'red',
            width: '100%',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;
